// What THIS runtime renders when served (7.6H; widened in 7.6I — Runtime
// Capability Contract, ADR-0029).
//
// RUNTIME-CONTRACT §7.2/§11: a runtime that does not implement a feature an
// artifact requires falls back to the approved static fallback — it never
// "ignores" a capability. The registry below is the single list; widening it
// is incremental work in the DOM renderer, with no contract change.
//
// The SAME check runs on a compiled artifact (boot, delivery diagnostics) and
// on an authoring document (the Studio can tell the author, before
// publishing, that a format will serve its fallback): `runtimeGaps` lists
// every gap with a closed kind, never only the first one.

import {
  capabilityProfile,
  type ArtifactLayoutV1,
  type CreativeDocument,
  type DeliveryArtifactV1,
} from "@interactads/creative-model";

// 1.2.3, not 1.2.2: dev/apparmor-runner-hardening (924aa7d) holds a different, never deployed 1.2.2; a runtime
// URL is immutable, so a version name is never reused for other content.
export const RUNTIME_VERSION = "1.2.3";
export const RUNTIME_MAJOR = 1;

/** capabilityProfile features the served DOM renderer implements (1.1.0). */
export const RUNTIME_FEATURES_V1: ReadonlySet<string> = new Set([
  "multi_layout",
  "multi_scene",
  "animation",
  "interaction",
  "state",
  "custom_events",
  "randomness",
  // 7.6I
  "video",
  "audio",
  "carousel",
  "form",
  "survey",
  "progress",
  "countdown",
  "drag",
  "game",
  "game_module",
  "game_targets",
  "game_pairs",
  "game_drops",
  "game_score",
  "game_lives",
  "game_timer",
  "game_runner",
  "game_collisions",
  "game_spawn",
  "game_energy",
  "parallax",
  // 1.2.0 (ADR-0043): AI-generated HTML5 served in an isolated frame.
  "html5",
]);

/** Element types the served DOM renderer draws (1.1.0). */
export const RUNTIME_ELEMENT_TYPES: ReadonlySet<string> = new Set([
  "text",
  "button",
  "shape",
  "image",
  "hotspot",
  "group",
  "video",
  "audio",
  "field",
  "component",
]);
/** Components drawn (exact registry references; another major is a gap, never a guess). */
export const RUNTIME_COMPONENTS: ReadonlySet<string> = new Set([
  "ia.carousel@1",
  "ia.survey-question@1",
  "ia.progress@1",
  "ia.countdown@1",
]);
/** Element behaviors executed: only `draggable` (game pieces and free drag). */
export const RUNTIME_BEHAVIORS: ReadonlySet<string> = new Set(["draggable"]);
/**
 * Actions and triggers of the generic game layer (AG-O1) that neither the
 * Studio preview nor the served runtime executes: `move`/`collider`/
 * `follow-pointer`/`tappable-target` behaviors, `spawn`/`despawn`, collision
 * triggers. A document that uses them is VALID and serves its fallback.
 */
export const UNEXECUTED_ACTIONS: ReadonlySet<string> = new Set(["spawn", "despawn"]);
export const UNEXECUTED_TRIGGERS: ReadonlySet<string> = new Set(["collision.enter", "collision.exit"]);

export type RuntimeGapKind =
  | "runtime_version"
  | "no_layout"
  | "responsive_layout"
  | "feature"
  | "element"
  | "component"
  | "behavior"
  | "action"
  | "trigger";

export interface RuntimeGap {
  readonly kind: RuntimeGapKind;
  /** Closed vocabulary (feature / type / component / behavior / action / trigger name). */
  readonly what: string;
  readonly element?: string;
}

export type Support =
  | { readonly ok: true; readonly layout: ArtifactLayoutV1 }
  | {
      readonly ok: false;
      readonly cause: "unsupported" | "no_layout";
      /** First gap found, for local debugging only (events carry closed codes; diagnostics derive the rest). */
      readonly detail: string;
      readonly gaps: readonly RuntimeGap[];
    };

const semver = (v: string) => v.split(".").map((n) => Number.parseInt(n, 10));
function atLeast(have: string, need: string): boolean {
  const [a, b] = [semver(have), semver(need)];
  for (let i = 0; i < 3; i++) {
    if ((a[i] ?? 0) !== (b[i] ?? 0)) return (a[i] ?? 0) > (b[i] ?? 0);
  }
  return true;
}

type Json = Record<string, unknown>;

interface Shape {
  readonly features: readonly string[];
  readonly sizingMode: string;
  readonly elements: readonly Json[];
  readonly rules: readonly Json[];
}

function gapsOf(input: Shape): RuntimeGap[] {
  const gaps: RuntimeGap[] = [];
  if (input.sizingMode !== "fixed") gaps.push({ kind: "responsive_layout", what: input.sizingMode });
  for (const f of input.features)
    if (!RUNTIME_FEATURES_V1.has(f) && f !== "responsive") gaps.push({ kind: "feature", what: f });
  for (const el of input.elements) {
    const id = String(el.id);
    const type = String(el.type);
    if (!RUNTIME_ELEMENT_TYPES.has(type)) gaps.push({ kind: "element", what: type, element: id });
    if (type === "component" && !RUNTIME_COMPONENTS.has(String(el.component)))
      gaps.push({ kind: "component", what: String(el.component), element: id });
    for (const b of (el.behaviors as Json[] | undefined) ?? [])
      if (!RUNTIME_BEHAVIORS.has(String(b.type))) gaps.push({ kind: "behavior", what: String(b.type), element: id });
  }
  const actions = (list: readonly Json[]): string[] =>
    list.flatMap((a) => [String(a.type), ...(a.type === "after" ? actions((a.do as Json[] | undefined) ?? []) : [])]);
  for (const r of input.rules) {
    const on = String((r.on as Json | undefined)?.type);
    if (UNEXECUTED_TRIGGERS.has(on)) gaps.push({ kind: "trigger", what: on });
    for (const a of actions((r.do as Json[] | undefined) ?? []))
      if (UNEXECUTED_ACTIONS.has(a)) gaps.push({ kind: "action", what: a });
  }
  // One entry per distinct gap (a document with 30 movers is one "behavior move" gap per element).
  const seen = new Set<string>();
  return gaps.filter((g) => {
    const k = `${g.kind}|${g.what}|${g.element ?? ""}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Every gap between an artifact variant and this runtime (empty = rendered interactively). */
export function runtimeGaps(artifact: DeliveryArtifactV1, layoutId: string): RuntimeGap[] {
  if (artifact.requires.runtimeMajor !== RUNTIME_MAJOR || !atLeast(RUNTIME_VERSION, artifact.requires.minRuntime))
    return [{ kind: "runtime_version", what: artifact.requires.minRuntime }];
  const layout = artifact.layouts.find((l) => l.id === layoutId);
  if (!layout) return [{ kind: "no_layout", what: layoutId }];
  return gapsOf({
    features: artifact.requires.features,
    sizingMode: layout.sizing.mode,
    elements: artifact.elements.filter((el) => (el.id as string) in layout.elements),
    rules: artifact.rules,
  });
}

export function supportOf(artifact: DeliveryArtifactV1, layoutId: string): Support {
  const gaps = runtimeGaps(artifact, layoutId);
  const layout = artifact.layouts.find((l) => l.id === layoutId);
  if (gaps.length === 0 && layout) return { ok: true, layout };
  const first = gaps[0] as RuntimeGap;
  return {
    ok: false,
    cause: first.kind === "no_layout" ? "no_layout" : "unsupported",
    detail: `${first.kind} ${first.what}`,
    gaps,
  };
}

/** Gaps of every variant of an artifact (delivery diagnostics: WHY a Distribution serves its fallback). */
export function runtimeSupportReport(artifact: DeliveryArtifactV1): Record<string, readonly RuntimeGap[]> {
  return Object.fromEntries(artifact.layouts.map((l) => [l.id, runtimeGaps(artifact, l.id)]));
}

/**
 * The same check on an AUTHORING document for one format variant (the Studio
 * warns before publishing). Features are the document's capability profile;
 * a layout that does not exist is `no_layout`.
 */
export function documentRuntimeGaps(doc: CreativeDocument, layoutId: string): RuntimeGap[] {
  const layout = doc.layouts.find((l) => l.id === layoutId);
  if (!layout) return [{ kind: "no_layout", what: layoutId }];
  return gapsOf({
    features: capabilityProfile(doc).features,
    sizingMode: layout.sizing.mode,
    elements: doc.elements,
    rules: doc.rules ?? [],
  });
}
